import { Stack, TextField } from '@mui/material';
import { MprcContainer, MprcFormSubHeader, MprcRequestErrorReport, MprcRequestIndicator, MprcStringToHtml } from 'components';
import { MprcDateTimePicker, formatDateTime } from 'components/MprcDateTimePicker';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { MprcSimpleDialog } from 'components/MprcSimpleDialog';
import { MprcSwitch } from 'components/MprcSwitch';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { InstructorAppointmentResponse } from 'generated/CoreApi_v1';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    subject?: string[] | null;
    startDate?: string[] | null;
    endDate?: string[] | null;
}
interface IProps {
    instructorId: string;
    appointmentId: string | null;
    onClose: (success?: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////

function buildValidationFields(currentValidation: IValidationFields, viewData: InstructorAppointmentResponse): IValidationFields {

    var newFormValidation = { ...currentValidation };

    newFormValidation.subject = viewData.subject != undefined && viewData.subject.length > 0 ? null : [""];
    newFormValidation.startDate = viewData.startDate != null && moment(viewData.startDate).isValid() && viewData.startDate >= new Date() ? null : [""];
    newFormValidation.endDate = viewData.endDate != null && moment(viewData.endDate).isValid() && viewData.endDate > viewData.startDate ? null : [""];

    return newFormValidation;
}

////////////////////////////////////////////////////////////////////////////

export const CreateOrUpdateAppointmentDialog: React.FC<IProps> = ({ instructorId, appointmentId, onClose }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(appointmentId != null);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewData, setViewData] = useState<InstructorAppointmentResponse>({} as InstructorAppointmentResponse);
    const [viewError, setViewError] = useState<any>();
    const [formValidation, setFormValidation] = useState<IValidationFields>({} as IValidationFields);

    const canSave = useMemo(() => Object.values(formValidation).filter((x) => x !== null).length == 0, [formValidation]);

    ////////////////////////////////////////////////////////////////////////////

    const handleUpdateRecord = (name: keyof InstructorAppointmentResponse, v: any) => {
        setViewData(() => {

            if (name == "allDay" && v == true) {
                return {
                    ...viewData,
                    allDay: true,
                    startDate: viewData.startDate != null ? moment(formatDateTime(viewData.startDate, null, "initialDate")).toDate() : null!,
                    endDate: viewData.endDate != null ? moment(formatDateTime(viewData.endDate, null, "endDate")).toDate() : null!
                };
            }

            return { ...viewData, [name]: v };
        });
    };

    const handleSave = () => {
        setSaving(true);
        api.v1.instructorManagement.createOrUpdateAppointment({
            appointmentId: appointmentId,
            instructorId,
            subject: viewData.subject,
            startDate: viewData.startDate,
            endDate: viewData.endDate,
            timeZoneId: moment.tz.guess()
        }).then(({ data: { success, hasConflict } }) => {
            if (success) {
                if (appointmentId == null)
                    enqueueSnackbar(t("Instructor.CreateOrUpdateAppointmentDialog.SaveSuccessMessage"), { variant: "success" });
                else
                    enqueueSnackbar(t("Instructor.CreateOrUpdateAppointmentDialog.UpdateSuccessMessage"), { variant: "success" });

                if (hasConflict) enqueueSnackbar(t("Instructor.CreateOrUpdateAppointmentDialog.NewConflictMessage"), { variant: "warning" });

                onClose(true);
            }
            else {
                enqueueSnackbar(t("Instructor.CreateOrUpdateAppointmentDialog.SaveErrorMessage"), { variant: "error" });
            }
        }).catch(e => {
            var r = MprcResolveRequestErrors(e);
            if (r.fieldsWithError && Object.keys(r.fieldsWithError).length > 0) {
                setFormValidation({
                    ...formValidation,
                    subject: r.fieldsWithError["subject"] ?? formValidation.subject,
                    startDate: r.fieldsWithError["startDate"] ?? formValidation.startDate,
                    endDate: r.fieldsWithError["endDate"] ?? formValidation.endDate
                });
            } else {
                setViewError(e);
            }
        })
            .finally(() => setSaving(false));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function loadData() {
            try {
                const response = await api.v1.instructorManagement.getAppointment({ appointmentId: appointmentId! });
                setViewData(response.data);
            } catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) loadData();
    }, [loading]);

    useEffect(() => {
        setFormValidation(state => buildValidationFields(state, viewData));
    }, [viewData]);

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={loading || saving} type={saving ? "post" : "request"} />
            <MprcRequestErrorReport error={viewError} />
            <MprcSimpleDialog
                width="sm"
                title={t("Instructor.CreateOrUpdateAppointmentDialog.Title")}
                subtitle={t("Instructor.CreateOrUpdateAppointmentDialog.Subtitle")}
                message={<>
                    <MprcContainer>
                        <MprcFormSubHeader title={t("Instructor.CreateOrUpdateAppointmentDialog.SubjectLabel") + " *"} />
                        <TextField
                            required
                            autoFocus
                            fullWidth
                            placeholder={t("Instructor.CreateOrUpdateAppointmentDialog.SubjectPlaceholder")}
                            autoComplete="off"
                            value={viewData.subject || ""}
                            onChange={e => handleUpdateRecord("subject", e.target.value)}
                            error={viewData.subject !== undefined && !!formValidation.subject}
                            helperText={viewData.subject !== undefined && <MprcStringToHtml text={formValidation?.subject} />}
                        />
                    </MprcContainer>
                    <MprcContainer>
                        <MprcFormSubHeader title={t("Instructor.CreateOrUpdateAppointmentDialog.PeriodLabel") + " *"} />
                        <Stack direction="row" spacing={5} alignItems="center">
                            <MprcDateTimePicker
                                value={viewData.startDate || null}
                                minDate={new Date()}
                                format="initialDate"
                                dateLabel={t("Instructor.CreateOrUpdateAppointmentDialog.StartDateLabel")}
                                onChange={(date, validate) => {
                                    handleUpdateRecord("startDate", date);
                                    setFormValidation(prevState => ({ ...prevState, startDate: validate ? null : [""] }));
                                }}
                                disabledOnlyTime={viewData.allDay}
                                error={viewData.startDate !== undefined && !!formValidation.startDate}
                                helperText={viewData.startDate !== undefined && <MprcStringToHtml text={formValidation?.startDate} />}
                            />
                            <MprcSwitch
                                label={t("Instructor.CreateOrUpdateAppointmentDialog.AllDayLabel")}
                                checked={viewData.allDay || false}
                                onChange={() => handleUpdateRecord("allDay", !viewData.allDay)}
                            />
                        </Stack>
                        <MprcDateTimePicker
                            value={viewData.endDate || null}
                            minDate={viewData.startDate || new Date()}
                            format="endDate"
                            dateLabel={t("Instructor.CreateOrUpdateAppointmentDialog.EndDateLabel")}
                            onChange={date => handleUpdateRecord("endDate", date)}
                            disabledOnlyTime={viewData.allDay}
                            error={viewData.endDate !== undefined && !!formValidation.endDate}
                            helperText={viewData.endDate !== undefined && <MprcStringToHtml text={formValidation?.endDate} />}
                        />
                    </MprcContainer>
                </>
                }
                onClose={onClose}
                actions={[
                    <ButtonCancel variant="contained" onClick={() => onClose()} />,
                    <ButtonConfirm
                        variant="contained"
                        disabled={!canSave}
                        text={t("Instructor.CreateOrUpdateAppointmentDialog.SaveButtonText")}
                        onClick={handleSave}
                    />
                ]}
                actionsPosition="end"
            />
        </>
    );
};
